import { useCallback, useState } from 'react';

import * as srsly from '@sly-rentals/core';
import { useNetwork } from '../lib/network-context';
import type { Query } from '../data/instructions.generated';

export type QueryRunnerStatus =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'ok'; data: unknown }
  | { kind: 'error'; message: string };

interface UseQueryRunnerResult {
  status: QueryRunnerStatus;
  run: (query: Query, values: Record<string, string>) => Promise<void>;
  reset: () => void;
}

// Same coerce helper the tx runner uses — strings that parse as JSON
// (or relaxed JS-literal form) are passed through, otherwise plain
// string / number / boolean fallback. Duplicated rather than imported
// to keep the two runners independent.
function jsLiteralToJson(raw: string): string {
  return raw
    .replace(/(\d)_(?=\d)/g, '$1')
    .replace(/([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g, '$1"$2":')
    .replace(/'((?:[^'\\]|\\.)*)'/g, '"$1"')
    .replace(/,(\s*[}\]])/g, '$1');
}

function coerce(raw: string): unknown {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === 'null') return null;
  if (/^-?\d[\d_]*(\.\d+)?$/.test(trimmed)) {
    return Number(trimmed.replace(/_/g, ''));
  }
  if (/^[{[]/.test(trimmed) || /^["']/.test(trimmed)) {
    try {
      return JSON.parse(trimmed);
    } catch {
      try {
        return JSON.parse(jsLiteralToJson(trimmed));
      } catch {
        return trimmed;
      }
    }
  }
  return trimmed;
}

// Build the call-arg from the form values. If the query has a single
// non-object first arg (`tickBudget(ticks)`,
// `estimateBorrowerSetupCost(rpcUrl)`), the catalog leaves `params`
// empty and we pass nothing — same as a bare `fn()`. Otherwise we
// gather every filled field into an object-arg.
function buildArg(query: Query, values: Record<string, string>): unknown {
  if (query.params.length === 0) return undefined;
  const obj: Record<string, unknown> = {};
  for (const p of query.params) {
    const v = coerce(values[p.name] ?? '');
    if (v === undefined && p.optional) continue;
    obj[p.name] = v;
  }
  return obj;
}

// JSON-safe stringification — bigints become decimal strings so the
// JSON pretty-printer can render them.
function jsonSafe<T>(value: T): unknown {
  return JSON.parse(JSON.stringify(value, (_k, v) => (typeof v === 'bigint' ? v.toString() : v)));
}

export function useQueryRunner(): UseQueryRunnerResult {
  const [status, setStatus] = useState<QueryRunnerStatus>({ kind: 'idle' });
  const { rpcUrl, rpcReady } = useNetwork();

  const reset = useCallback(() => setStatus({ kind: 'idle' }), []);

  const run = useCallback(
    async (query: Query, values: Record<string, string>) => {
      setStatus({ kind: 'running' });
      try {
        const fn = (srsly as unknown as Record<string, unknown>)[query.name];
        if (typeof fn !== 'function') {
          throw new Error(`SDK does not export "${query.name}".`);
        }
        if (!rpcReady) {
          throw new Error('No RPC URL set — choose a network or enter a custom RPC URL.');
        }
        // Apply the playground's current RPC so queries land on the
        // selected cluster (mainnet/atlasnet/devnet/custom).
        if (typeof srsly.setSdkConfig === 'function') {
          srsly.setSdkConfig({ rpcUrl });
        }
        const arg = buildArg(query, values);
        const result = await (arg === undefined
          ? (fn as () => unknown)()
          : (fn as (a: unknown) => unknown)(arg));
        setStatus({ kind: 'ok', data: jsonSafe(result) });
      } catch (err) {
        setStatus({
          kind: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [rpcUrl, rpcReady]
  );

  return { status, run, reset };
}
